import { ApplicationDocumentWithAdditionalApplicantData } from 'types';

import { Application, ApplicationSuccessResponse } from '../types/Applications';

import { api } from './api';

interface GetApplicationsQueryParams {
  accountId: string;
  inviteToken?: string | null;
}

export const applicationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getApplications: build.query<ApplicationDocumentWithAdditionalApplicantData[], GetApplicationsQueryParams>({
      query: ({ accountId, inviteToken }) => ({
        url: `accounts/${accountId}/applications`,
        params: inviteToken ? { inviteToken } : undefined,
      }),
      providesTags: () => [{ type: 'Applications', id: 'LIST' }],
    }),
    getExternalApplicationUrl: build.query<{ url: string }, string>({
      query: (id) => ({
        url: `accounts/${id}/applications/external`,
      }),
    }),
    createApplication: build.mutation<ApplicationSuccessResponse, any>({
      query: ({ application, accountId, inviteToken, turnstileToken }) => ({
        url: `accounts/${accountId}/applications`,
        method: 'POST',
        body: { ...application, ...(turnstileToken ? { turnstileToken } : {}) },
        params: inviteToken ? { inviteToken } : undefined,
      }),
      invalidatesTags: (_result, _error, { accountId, inviteToken }) =>
        inviteToken
          ? []
          : [
              { type: 'Accounts', id: accountId },
              { type: 'Applications', id: 'LIST' },
              { type: 'CreditReportSummaries', id: 'LIST' },
              { type: 'AccountMilestones', id: accountId },
              { type: 'AccountStipulations', id: 'LIST' },
              { type: 'Pricing', id: accountId },
            ],
    }),
    overrideApplication: build.mutation<Application, any>({
      query: ({ accountId, applicationId }) => ({
        url: `accounts/${accountId}/applications/${applicationId}/credit-override`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: 'Applications', id: 'LIST' },
        { type: 'AccountMilestones', id: accountId },
        { type: 'Accounts', id: accountId },
      ],
    }),
    sendExternalApplicationUrl: build.mutation<void, { accountId: string; email: string; firstName: string }>({
      query: ({ accountId, email, firstName }) => ({
        url: `accounts/${accountId}/applications/external/send`,
        method: 'POST',
        body: { email, firstName },
      }),
    }),
    verifyOwnershipForApplication: build.mutation<Application, any>({
      query: ({ applicationId, accountId }) => ({
        url: `accounts/${accountId}/applications/${applicationId}/verify-ownership`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: 'Applications', id: 'LIST' },
        { type: 'AccountMilestones', id: accountId },
        { type: 'Accounts', id: accountId },
        { type: 'PropertyReports', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useLazyGetExternalApplicationUrlQuery,
  useCreateApplicationMutation,
  useOverrideApplicationMutation,
  useSendExternalApplicationUrlMutation,
  useVerifyOwnershipForApplicationMutation,
} = applicationsApi;
